'use client'

import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Gift, X } from 'lucide-react'
import { useAuth } from '@/components/AuthProvider'
import { Button } from '@/components/ui/Button'
import { track } from '@/lib/analytics'

// "Refer a friend" pop-up. Opened from the profile menu, the Usage page
// (window event 'cvcircuit:openReferral') or a ?refer=1 link (the reward
// email, the extension). Rules live in backend/src/utils/referrals.ts.

export const OPEN_REFERRAL_EVENT = 'cvcircuit:openReferral'

const formatMoney = (minor: number, currency = 'gbp') => {
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency.toUpperCase() }).format(minor / 100)
  } catch {
    return `${(minor / 100).toFixed(2)} ${currency.toUpperCase()}`
  }
}

const formatDate = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })

export function ReferFriendModal() {
  const { user, refreshUser } = useAuth()
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)

  const show = () => {
    setOpen(true)
    setCopied(false)
    // Fresh code and totals: the cached account may predate them.
    void refreshUser()
  }

  useEffect(() => {
    const handler = () => show()
    window.addEventListener(OPEN_REFERRAL_EVENT, handler)
    return () => window.removeEventListener(OPEN_REFERRAL_EVENT, handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Reads window.location rather than useSearchParams: this is mounted on
  // pages without a Suspense boundary (see ProfileDropdown).
  useEffect(() => {
    if (!user) return
    const params = new URLSearchParams(window.location.search)
    if (params.get('refer') !== '1') return
    show()
    params.delete('refer')
    window.history.replaceState(null, '', params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId])

  if (!open || !user) return null

  const code = user.referralCode
  const link = code ? `${window.location.origin}/${code}` : ''
  const message = `I've been using CVCircuit to fill in long UK job application forms. It saves a lot of retyping: ${link}`
  const friends = user.referralFriends ?? 0
  const credit = user.referralCreditMinor ?? 0
  const days = user.referralBonusDays ?? 0
  const bonusUntil = user.bonusProUntil && Date.parse(user.bonusProUntil) > Date.now() ? user.bonusProUntil : null

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
      track('referral_share', { method: 'copy' })
    } catch { }
  }

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-8" onClick={() => setOpen(false)}>
      <div className="relative w-full max-w-lg rounded-lg bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="refer-title">
        <button onClick={() => setOpen(false)} className="absolute right-4 top-4 p-1 text-gray-400 hover:text-gray-600" aria-label="Close">
          <X className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <Gift className="w-5 h-5 text-primary-600" />
          <h2 id="refer-title" className="text-xl font-bold text-gray-800">Refer a friend</h2>
        </div>
        <p className="mt-2 text-sm text-gray-600">
          Know someone filling in long application forms? Share your link. When a friend subscribes:
        </p>
        <ul className="mt-3 space-y-2 text-sm text-gray-700">
          <li className="flex gap-2"><span className="text-primary-600 font-bold">•</span><span><strong>On a subscription:</strong> you get 25% off your next month. Every friend adds another 25%, and anything over a full month carries over to the bill after.</span></li>
          <li className="flex gap-2"><span className="text-primary-600 font-bold">•</span><span><strong>Not subscribed, or your plan is ending:</strong> you get 8 days of Pro instead, straight away.</span></li>
        </ul>

        <div className="mt-5">
          <label htmlFor="refer-link" className="text-xs font-semibold text-gray-500">Your link</label>
          <div className="mt-1 flex gap-2">
            <input
              id="refer-link"
              readOnly
              value={link || 'Creating your link…'}
              onFocus={(e) => e.currentTarget.select()}
              className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-800"
            />
            <Button onClick={() => void copy()} disabled={!link}>{copied ? 'Copied' : 'Copy'}</Button>
          </div>
          {link && (
            <div className="mt-3 flex flex-wrap gap-2 text-sm">
              <a
                href={`https://wa.me/?text=${encodeURIComponent(message)}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track('referral_share', { method: 'whatsapp' })}
                className="rounded-lg border border-gray-300 px-3 py-1.5 font-medium text-gray-700 hover:bg-gray-50"
              >
                Share on WhatsApp
              </a>
              <a
                href={`mailto:?subject=${encodeURIComponent('A tool for long job application forms')}&body=${encodeURIComponent(message)}`}
                onClick={() => track('referral_share', { method: 'email' })}
                className="rounded-lg border border-gray-300 px-3 py-1.5 font-medium text-gray-700 hover:bg-gray-50"
              >
                Share by email
              </a>
            </div>
          )}
        </div>

        <div className="mt-5 grid grid-cols-3 gap-3 rounded-lg bg-gray-50 p-4 text-center">
          <div>
            <p className="text-lg font-bold text-gray-900">{friends}</p>
            <p className="text-xs text-gray-500">{friends === 1 ? 'friend subscribed' : 'friends subscribed'}</p>
          </div>
          <div>
            <p className="text-lg font-bold text-gray-900">{formatMoney(credit, user.referralCreditCurrency)}</p>
            <p className="text-xs text-gray-500">off your bills</p>
          </div>
          <div>
            <p className="text-lg font-bold text-gray-900">{days}</p>
            <p className="text-xs text-gray-500">days of Pro</p>
          </div>
        </div>
        {bonusUntil && (
          <p className="mt-3 text-sm text-gray-600">Your Pro days run until {formatDate(bonusUntil)}.</p>
        )}

        <p className="mt-4 text-xs text-gray-500">
          Your friend pays the normal price. Rewards are added when their first payment goes through, and taken back if that payment is refunded.
        </p>
      </div>
    </div>,
    document.body,
  )
}
